// Central config for the self-schedule experiment (WEBDEV-3681 and future
// rollouts). The mechanism — redirecting consult-form CTAs to the external
// /schedule app and swapping the on-page form for a hidden cloudamp stub — is
// page-agnostic. To enable it on a new page, add that page's path below; the
// Layout-level CTA interceptor and form suppression pick it up automatically.
//
// Always test membership via isSelfSchedulePath() rather than reading this array
// directly — the predicate normalizes trailing slashes, a raw `.includes()` does not.
export const SELF_SCHEDULE_PATHS = ['/specials', '/', '/lp/google-v2'];

// On-page anchors that scroll to a consult form. CTAs targeting any of these
// (on a self-schedule path, while the variant is active) are redirected to
// /schedule instead of scrolling to the now-removed form.
// - #consult        : standard ConsultationForm anchor (most pages, incl. /specials)
// - #googlelp-quote : the non-legacy ConsultationForm anchor used on / and /lp/google-v2
export const CONSULT_FORM_ANCHORS = ['#consult', '#googlelp-quote'];

// Container ids (bare, for getElementById — unlike CONSULT_FORM_ANCHORS above)
// of the on-page lead forms. Exported one-per-form and rendered from here by the
// form components themselves, so the id a form carries and the id the observer
// looks for cannot drift apart — renaming one is a compile-time move, not a
// silent break. Note CONSULT_FORM_ID's *value* is not free to change — it is
// also used as a CSS selector (Consult.css, global.css).
export const CONSULT_FORM_ID = 'consult-form'; // legacy/standard ConsultationForm section
export const ON_PAGE_SELF_SCHEDULE_FORM_ID = 'on-page-self-schedule-form'; // the variant '2' on-page form

// The containers the mobile sticky CTA hides behind, so the floating button
// doesn't overlap or compete with a form's own CTA (WEBDEV-3627). Variant '2'
// swaps the legacy form for the self-schedule one client-side and the two don't
// share an id, so the sticky observes whichever of these is actually in the DOM
// rather than a single hard-coded id (WEBDEV-4239). The variant '1' stub needs
// no entry — it renders hidden, with no id.
export const ON_PAGE_FORM_CONTAINER_IDS = [CONSULT_FORM_ID, ON_PAGE_SELF_SCHEDULE_FORM_ID];

// Treat "/foo" and "/foo/" as the same path (Gatsby serves trailing slashes).
function normalizePath(pathname: string): string {
    return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function isSelfSchedulePath(pathname: string): boolean {
    return SELF_SCHEDULE_PATHS.includes(normalizePath(pathname));
}

// Corporate-side kill-switch for the on-page form specifically (WEBDEV-4043),
// independent of the SSA-side flag and of the Optimizely variant itself — lets
// us go dark on just the form (e.g. a form-side bug) without a campaign change
// or touching the SSA. Off (or unset) falls back to the hidden stub even under
// variant '2' (see useSelfScheduleFlags). A GATSBY_-prefixed env var is inlined
// at build time, so flipping it requires a rebuild, not just a redeploy.
export const ON_PAGE_FORM_ENABLED = process.env.GATSBY_ONPAGE_FORM_ENABLED === 'true';

export type OnPageFormDefault = 'standard' | 'ssa_onpage';

// Anything but 'ssa_onpage' (unset included) keeps the pre-WEBDEV-4374 behavior: standard.
function parseOnPageFormDefault(value: string | undefined): OnPageFormDefault {
    return value?.trim() === 'ssa_onpage' ? 'ssa_onpage' : 'standard';
}

// Per-page default experience (WEBDEV-4374; / and /specials added in
// WEBDEV-4511), used until Optimizely sends an explicit variant — which then
// wins, including '0' reverting to the standard form. `ssa_onpage` behaves as
// variant '2': on-page form, CTAs to /schedule. The flags are independent: each
// page reads only its own. An entry here only takes effect if its path is also
// in SELF_SCHEDULE_PATHS (useSelfScheduleFlags checks that first) and while
// ON_PAGE_FORM_ENABLED is on. Build-time like ON_PAGE_FORM_ENABLED, and each env
// var must be read by its literal name for Gatsby to inline it. Pause the
// page's Optimizely campaign before flipping one: the static HTML then carries
// the on-page form, so a visitor bucketed into control afterwards sees it swap
// out after load.
const ON_PAGE_FORM_DEFAULTS: Partial<Record<string, OnPageFormDefault>> = {
    '/': parseOnPageFormDefault(process.env.GATSBY_ONPAGE_FORM_HOMEPAGE_VARIANT),
    '/specials': parseOnPageFormDefault(process.env.GATSBY_ONPAGE_FORM_SPECIALS_VARIANT),
    '/lp/google-v2': parseOnPageFormDefault(process.env.GATSBY_ONPAGE_FORM_LP_GOOGLE_V2_VARIANT),
};

export function getOnPageFormDefault(pathname: string): OnPageFormDefault {
    return ON_PAGE_FORM_DEFAULTS[normalizePath(pathname)] || 'standard';
}

// Kill-switch for the on-page form's "Get A Custom Quote" secondary CTA
// (WEBDEV-4259). Off (or unset) forces every placement — including an Optimizely
// override — back to the default "Ask A Question" behavior, so the quote path can
// be taken dark without a campaign change. Build-time like ON_PAGE_FORM_ENABLED:
// flipping it requires a rebuild.
export const ON_PAGE_CUSTOM_QUOTE_ENABLED = process.env.GATSBY_ONPAGE_CUSTOM_QUOTE_ENABLED === 'true';
